import { MethodResult, Problem, Step } from './types';
import { cloneAlloc, makeMatrix, totalCost } from './utils';

// Método de la Esquina Noroeste.
// Comienza en la celda superior izquierda y asigna el máximo posible,
// avanzando hacia la derecha o hacia abajo según se agote demanda u oferta.
export function northwestCorner(p: Problem): MethodResult {
  const m = p.supply.length;
  const n = p.demand.length;
  const alloc = makeMatrix<number | null>(m, n, null);
  const supply = p.supply.slice();
  const demand = p.demand.slice();
  const steps: Step[] = [];

  steps.push({
    title: 'Inicio',
    description:
      'Comenzamos en la esquina noroeste (superior izquierda). Se asignará el máximo posible a cada celda recorriendo de izquierda a derecha y de arriba hacia abajo.',
    alloc: cloneAlloc(alloc),
    remainingSupply: supply.slice(),
    remainingDemand: demand.slice(),
  });

  let i = 0;
  let j = 0;
  let count = 1;

  while (i < m && j < n) {
    const amount = Math.min(supply[i], demand[j]);
    alloc[i][j] = amount;
    supply[i] -= amount;
    demand[j] -= amount;

    let reason: string;
    if (supply[i] === 0 && demand[j] === 0) {
      reason = `Se agotan simultáneamente la oferta de ${p.sources[i]} y la demanda de ${p.dests[j]}.`;
    } else if (supply[i] === 0) {
      reason = `Se agota la oferta de ${p.sources[i]}; bajamos a la siguiente fila.`;
    } else {
      reason = `Se satisface la demanda de ${p.dests[j]}; avanzamos a la siguiente columna.`;
    }

    steps.push({
      title: `Paso ${count}`,
      description: `Celda (${p.sources[i]}, ${p.dests[j]}): asignamos mín(oferta=${
        supply[i] + amount
      }, demanda=${demand[j] + amount}) = ${amount}. ${reason}`,
      alloc: cloneAlloc(alloc),
      remainingSupply: supply.slice(),
      remainingDemand: demand.slice(),
      highlight: { row: i, col: j },
    });
    count++;

    // Avanzar: si la oferta se agotó bajamos; si no, avanzamos columna.
    if (supply[i] === 0 && demand[j] === 0) {
      // En caso de empate, avanzamos columna salvo que sea la última.
      if (j < n - 1) j++;
      else i++;
    } else if (supply[i] === 0) {
      i++;
    } else {
      j++;
    }
  }

  const cost = totalCost(alloc, p.costs);
  steps.push({
    title: 'Solución inicial',
    description: `Se completaron todas las asignaciones. Costo total de la solución inicial = ${cost}.`,
    alloc: cloneAlloc(alloc),
    remainingSupply: supply.slice(),
    remainingDemand: demand.slice(),
  });

  return { alloc, steps, totalCost: cost };
}
